import { getServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import type { CrewMember } from '@/components/Widget/CrewWidget';
import { getPrimaryRole, rolesOnService, SYSTEM_ROLES } from '@/utils/roles';

import type { MappedPersonForSelection, Person } from './person.dto';

// The largest avatars these lists are drawn at — crew rows and the people pickers.
const CREW_AVATAR_BOX = 64;
const SELECTION_AVATAR_BOX = 40;

export const mapPeopleToCrewMembers = (people: Person[], vesselId: string | null): CrewMember[] => {
  if (!people.length || !vesselId) return [];

  return people
    .filter((person) => {
      const personServices = person.services ?? [];
      const vesselRole = personServices.find((r) => r.service_id === vesselId);
      return vesselRole && !SYSTEM_ROLES.has(vesselRole.role); // exclude Owner, SPs, Guests
    })
    .map((person) => ({
      id: person.id,
      firstName: person.person_info.first_name,
      lastName: person.person_info.last_name,
      avatar: imageVariantLink(person.avatar, CREW_AVATAR_BOX) ?? undefined,
      services: person.services ?? [],
    }));
};

/**
 * A person holds a different role on every service they belong to, so a role only means
 * something next to a vessel. Collapsing all of them to the most senior one made a captain
 * elsewhere outrank the mate they are here — `serviceId` scopes the answer to one vessel,
 * and defaults to the active one.
 */
export const mapPeopleForSelection = (
  people: Person[],
  serviceId: string = getServiceId(),
): MappedPersonForSelection[] => {
  if (!people.length) return [];

  return people.map((person) => {
    const role = getPrimaryRole(rolesOnService(person.services, serviceId));

    return {
      id: person.id,
      firstName: person.person_info.first_name,
      lastName: person.person_info.last_name,
      avatar: imageVariantLink(person.avatar, SELECTION_AVATAR_BOX) ?? '',
      role,
    };
  });
};
